<template>
  <dashboard-layout title="Like / Dislike Dashboard">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search…" />
    </template>

    <stat-grid>
      <stat-card label="Total feedback" :value="thumbs.length.toLocaleString('en-US')" icon="thumbs" accent />
      <stat-card label="Likes" :value="likeCount.toLocaleString('en-US')" icon="smile" />
      <stat-card label="Dislikes" :value="dislikeCount.toLocaleString('en-US')" icon="messagecircle" />
      <stat-card label="Distinct users" :value="distinctUsers.toLocaleString('en-US')" icon="users" />
    </stat-grid>

    <data-panel flush>
      <template #title>Feedback <span class="muted">({{ filteredThumbs.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="sortOrder" :options="sortOptions" />
      </template>

      <loading-state v-if="loading" label="Loading…" />
      <data-table
        v-else
        :columns="columns"
        :rows="filteredThumbs"
        row-key="id"
        clickable
        @row-click="handleRowClick"
      >
        <template #cell-feedbackThumb="{ row }">
          <span class="thumb-cell">
            <img v-if="row.feedbackThumb === 'up'" src="../assets/up.png" alt="Up" class="thumb-icon">
            <img v-else-if="row.feedbackThumb === 'down'" src="../assets/down.png" alt="Down" class="thumb-icon">
            <span>{{ row.feedbackThumb === 'up' ? 'Up' : 'Down' }}</span>
          </span>
        </template>
        <template #cell-content="{ row }">
          <span class="msg" :title="row.content">{{ truncateText(row.content, 100) }}</span>
        </template>
        <template #cell-createdAt="{ row }">
          <span class="nowrap">{{ formatTimestamp(row.createdAt) }}</span>
        </template>
        <template #cell-userId="{ row }">
          {{ truncateText(row.userId, 10) }}
        </template>
        <template #empty>No data</template>
      </data-table>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseThumbService } from '../services/firebaseThumbService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'

const columns = [
  { key: 'feedbackThumb', label: 'Type', width: '120px' },
  { key: 'content', label: 'Message' },
  { key: 'createdAt', label: 'Date', width: '150px' },
  { key: 'userId', label: 'User ID', width: '140px' }
]

const sortOptions = [
  { value: 'desc', label: 'Newest' },
  { value: 'asc', label: 'Oldest' }
]

export default {
  name: 'ThumbDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseThumbService()
    const thumbs = ref([])
    const searchQuery = ref('')
    const sortOrder = ref('desc')
    const loading = ref(true)

    const loadData = async () => {
      try {
        thumbs.value = await service.getAllThumbs()
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const likeCount = computed(() => thumbs.value.filter(t => t.feedbackThumb === 'up').length)
    const dislikeCount = computed(() => thumbs.value.filter(t => t.feedbackThumb === 'down').length)
    const distinctUsers = computed(() => new Set(thumbs.value.map(t => t.userId).filter(Boolean)).size)

    const filteredThumbs = computed(() => {
      const q = searchQuery.value.toLowerCase()
      const data = thumbs.value.filter(t => t.content?.toLowerCase().includes(q) || t.userId?.toLowerCase().includes(q))

      data.sort((a, b) => {
        const getTime = m => m.createdAt?.seconds ? m.createdAt.seconds * 1000 : new Date(m.createdAt).getTime()
        const diff = getTime(a) - getTime(b)
        return sortOrder.value === 'asc' ? diff : -diff
      })

      return data
    })

    const truncateText = (text, max) => {
      if (!text) return '-'
      return text.length > max ? text.substring(0, max) + '...' : text
    }

    const formatTimestamp = ts => {
      if (!ts) return '-'
      if (ts.seconds) {
        const d = new Date(ts.seconds * 1000)
        return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      }
      const d = new Date(ts)
      return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    const handleRowClick = (row, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/thumb/${row.id}`, '_blank')
      } else {
        router.push(`/thumb/${row.id}`)
      }
    }

    onMounted(loadData)

    return {
      thumbs, searchQuery, sortOrder, loading, columns, sortOptions,
      likeCount, dislikeCount, distinctUsers, filteredThumbs,
      truncateText, formatTimestamp, handleRowClick
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }
.msg {
  display: inline-block;
  max-width: 460px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.thumb-cell { display: inline-flex; align-items: center; gap: 6px; }
.thumb-icon { width: 20px; height: 20px; }
</style>
